import {useState} from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import { TextField, Alert, Stack, InputAdornment, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';

// ----------------------------------------------------------------------

type InitialValues = {
  password: string;
  confirmPassword: string;
  afterSubmit?: string;
};

type NewPasswordFormProps = {
  onSent: VoidFunction;
  onGetEmail: (value: string) => void;
};

export default function NewPasswordForm({ onSent, onGetEmail }: NewPasswordFormProps) {
  const { newPassword } = useAuth();
  const isMountedRef = useIsMountedRef();
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const NewPasswordSchema = Yup.object().shape({
    password: Yup.string().required('La contraseña es requerida'),
    confirmPassword: Yup.string().required().oneOf([Yup.ref('password')], 'Las contraseñas deben ser iguales'),
  });

  const formik = useFormik<InitialValues>({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: NewPasswordSchema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      try {
        
        if (isMountedRef.current) {
          const username = searchParams.get("username");
          const token = searchParams.get("token");

          if(username && token) {
            newPassword(values.password, username, token);
            onSent();
            onGetEmail(username);
          }
        }
        
      } catch (error:any) {
        console.error(error);

        if (isMountedRef.current) {
          setErrors({ afterSubmit: error.message });
          setSubmitting(false);
        }
      }
    }
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}

          <TextField
            fullWidth
            autoComplete="new-password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
          <TextField
            fullWidth
            autoComplete="confirm-password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            {...getFieldProps('confirmPassword')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.confirmPassword && errors.confirmPassword)}
            helperText={touched.confirmPassword && errors.confirmPassword}
          />

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            Restablecer contraseña
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
